import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { showToast } from "../../../../../../store/actions";
import TableWithStickHeader from "../../../../../Common/Table/TableWithStickHeader";
import { setPrevState } from "../../../../../../util/common";
import { ButtonUI, KMKDialog } from "../../../../../Common";

const columns = [
  { id: "id", label: "#", width: "70px", align: "left" },
  { id: "attribute", label: "Attribute", width: "150px", align: "left" },
  { id: "message", label: "Message", width: "420px", align: "left" },
];

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root.MuiDialog-paper": {
      maxWidth: "960px",
      width: "960px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
  },
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
    minHeight: "100px",
  },
  Toolbar: {
    width: "100%",
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
}));

export default function ErrorLog({
  setOpenErrorLogPopup,
  popupTitle,
  errorList,
  dataTableRef,
  templateDataRef,
  templateName,
  tagsRef,
  currentFields,
}) {
  const [errorState, setErrorState] = useState({
    errorList: [],
  });
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const handleCancel = () => {
    setOpenErrorLogPopup(false);
  };

  // Function to call on Proceed button click to load data by checking conflicted fields
  const handleProceedClick = () => {
    dataTableRef.current.deleteRow(currentFields);

    let referenceFromErrors = new Set();
    let referenceToErrors = new Set();
    let entityErrors = new Set();
    let kmkModelErrors = new Set();
    let dvModelErrors = new Set();
    let dqmModelErrors = new Set();

    errorList.forEach(({ type, field }) => {
      if (type === "entity") {
        entityErrors.add(field);
      } else if (type === "referenceTo") {
        referenceToErrors.add(field);
      } else if (type === "referenceFrom") {
        referenceFromErrors.add(field);
      } else if (type === "kmkModel") {
        kmkModelErrors.add(field);
      } else if (type === "dvModel") {
        dvModelErrors.add(field);
      } else if (type === "DQM") {
        dqmModelErrors.add(field);
      }
    });

    const selectedTemplate = templateDataRef.current.find(
      (item) => item.name.toLowerCase() === templateName.toLowerCase()
    );

    if (Object.keys(selectedTemplate).length > 0) {
      if (selectedTemplate?.fields.length) {
        // Remove mapping for not matched fields
        selectedTemplate.fields.forEach((field) => {
          if (referenceFromErrors.has(field.name)) {
            field.type = field.referenceFrom.field.type;
            field.length = field.referenceFrom.field.length;
            field.format = field.referenceFrom.field.format || "";
            field.customFormat = field.referenceFrom.field.customFormat || "";
            field.referenceFrom = {};
          }
          if (referenceToErrors.has(field.name)) field.referenceTo = [];
          if (dvModelErrors.has(field.name)) field.dataVaultKey = false;
          if (kmkModelErrors.has(field.name)) {
            field.primary_attribute = false;
            field.secondary_attribute = false;
          }
          if (entityErrors.has(field.name)) {
            field.entity_mapping = {};
            field.primary_attribute = false;
            field.secondary_attribute = false;
          }
          if (dqmModelErrors.has(field.name)) {
            field.dqm.refSource = false;
            field.dqm.refField = false;
            field.dqm.refAlert = false;
          }
        });

        dataTableRef.current.addRow(selectedTemplate.fields);
      }

      tagsRef.current.resetData();
      if (selectedTemplate?.tags?.length)
        tagsRef.current.setTagsFromParent(JSON.parse(JSON.stringify(selectedTemplate.tags)));

      dispatch(
        showToast({
          message: "Template loaded successfully.",
          type: "success",
        })
      );
    }
    setOpenErrorLogPopup(false);
  };

  useEffect(() => {
    let errorDataArr = [];

    errorList.length &&
      errorList.forEach((row, key) => {
        errorDataArr.push([
          {
            value: key + 1,
            align: "left",
          },
          {
            value: row.field,
            align: "left",
          },
          {
            value: row.message,
            align: "left",
          },
        ]);
      });

    setPrevState(setErrorState, { errorList: errorDataArr });
    return () => {
      setPrevState(setErrorState, { errorList: [] });
    };
  }, [errorList]);

  return (
    <KMKDialog className={classes.dialogPaper} open={true} onClose={handleCancel}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{popupTitle} </Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid sx={{ color: (theme) => theme.palette.error.main }} size={9}>
              There are some mismatch in mapping(s) or relationship(s). Are you sure you want to proceed?
            </Grid>
            <Grid size={3}>
              <DialogActions sx={{ padding: 0 }}>
                <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
                  Cancel
                </ButtonUI>
                <ButtonUI
                  onClick={() => {
                    handleProceedClick();
                  }}
                  variant="contained"
                  color="success"
                  style={{ marginLeft: 15 }}
                >
                  Proceed
                </ButtonUI>
              </DialogActions>
            </Grid>

            <Grid size={12}>
              <TableWithStickHeader
                {...{
                  columns: columns,
                  tableData: errorState.errorList,
                  tableMaxWidth: "912px",
                  tableMaxHeight: "350px",
                  noDataColspan: 2,
                  noDataMessage: "No error found",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </KMKDialog>
  );
}
